import {
  Box,
  Disclaimer,
  Div,
  H5,
  OutstandingBalance,
  OutstandingBanners,
  P,
  Separator,
  displayCentsInDollars,
  lightTheme,
} from '@vgrubs/components';
import { SERVICE_LABELS, formatDayTime } from '../helpers/receivablesLabels';

type Props = {
  current: OutstandingBalance;
};

/**
 * What the merchant is looking at for this location: the banners on their
 * tablet and in the app, the payment buttons they are offered and the bank
 * payments already on their way. Support needs it to talk them through it.
 *
 * Admin gets the full debt, SaaS included, even where the owner is not told
 * about it (vGrubs did not pay off for them over the last 30 days): the
 * backend's banners here are built from that admin view. So with
 * `saasProfitability.profitable` false the SaaS banner and SaaS payment
 * option are left out - the merchant has neither - and a line says why.
 */
export const MerchantView = ({ current }: Props) => {
  const saasHidden = current.saasProfitability?.profitable === false;
  const shownToMerchant = (service: string) =>
    !(saasHidden && service === 'saas');
  const banners = current.banners.filter((banner) =>
    shownToMerchant(banner.service)
  );
  const methods = current.paymentMethods.filter(
    (method) => method.visible && shownToMerchant(method.service ?? 'vDrive')
  );

  return (
    <Box $flex $column $gap="12px">
      <H5 $m="0">What the merchant sees</H5>
      {banners.length > 0 ? (
        <OutstandingBanners
          banners={banners}
          vDriveBalanceLocations={current.vDrive?.balanceLocations}
        />
      ) : (
        <Disclaimer $m="-8px 0 0 0" $color={lightTheme.lightGray}>
          No banners.
        </Disclaimer>
      )}
      {saasHidden && (current.saas?.total ?? 0) > 0 && (
        <Disclaimer $m="0" $color={lightTheme.darkYellow}>
          The SaaS fee is hidden from the owner, so they see no SaaS banner and
          no SaaS payment option.
        </Disclaimer>
      )}

      <Separator />

      <H5 $m="0">Payment options</H5>
      {methods.length > 0 ? (
        methods.map((method) => (
          <Div
            key={`${method.service}-${method.method}`}
            $flex
            $alignStart
            $between
            $gap="16px"
          >
            <Div>
              <P $m="0" $bold>
                {SERVICE_LABELS[method.service]}: {method.label}
              </P>
              <Disclaimer $m="0" $color={lightTheme.lightGray}>
                {method.timing}
              </Disclaimer>
            </Div>
            <Disclaimer
              $m="0"
              $right
              $color={method.available ? lightTheme.green : lightTheme.primary}
            >
              {method.available
                ? 'Available'
                : method.unavailableReason ?? 'Unavailable'}
            </Disclaimer>
          </Div>
        ))
      ) : (
        <Disclaimer $m="-8px 0 0 0" $color={lightTheme.lightGray}>
          No payment methods offered to the merchant.
        </Disclaimer>
      )}

      <Separator />

      <H5 $m="0">Bank payments in progress</H5>
      {current.pendingBankPayments.length > 0 ? (
        <>
          {current.pendingBankPayments.map((payment) => (
            <Div
              key={payment.clientPaymentId}
              $flex
              $alignStart
              $between
              $gap="16px"
            >
              <Div>
                <P $m="0">
                  {displayCentsInDollars(payment.overdraftAmount)} to the
                  balance
                </P>
                <Disclaimer $m="0" $color={lightTheme.lightGray}>
                  {formatDayTime(payment.createdAt)} -{' '}
                  {payment.autoInitiated
                    ? 'started by the system'
                    : 'started by the merchant'}
                  {` - payment #${payment.clientPaymentId}`}
                </Disclaimer>
              </Div>
              <Disclaimer $m="0" $right>
                {displayCentsInDollars(payment.amount)} debited
              </Disclaimer>
            </Div>
          ))}
          <Disclaimer $m="0" $color={lightTheme.lightGray}>
            {displayCentsInDollars(current.pendingBank.balanceCredit)} goes onto
            the balance when they clear, usually in 3-5 business days.
          </Disclaimer>
        </>
      ) : (
        <Disclaimer $m="-8px 0 0 0" $color={lightTheme.lightGray}>
          No bank payment is on its way right now.
        </Disclaimer>
      )}
    </Box>
  );
};
